import { styled } from "@linaria/react";
import type { ChangeEvent } from "react";
import React from "react";
import { color, fonts, radius } from "..";
import useControllableState from "../lib/hooks/useControllableState";
import OptionList from "../OptionList";
import type { PopperProps } from "../Popper";
import Popper from "../Popper";
import classNames from "classnames";
import t from "../i18n";
import { shadows, spacings } from "../Theme";
import SearchInputV2 from "../Input/SearchInputV2";
import { css } from "@linaria/core";

const OPENED = "opened";

interface P<T extends string> extends PopperProps {
  opened?: boolean;
  setOpened?: React.Dispatch<React.SetStateAction<boolean>>;
  defaultOpened?: boolean;
  children: React.ReactElement;
  filtered?: boolean;
  filterValue?: T;
  onFilterChange?: (value: string) => void;
}

const animationTimeout = 0.24;

const MenuSearch = css`
  grid-template-rows: 36px;
  grid-auto-rows: 36px;
  position: sticky;
  top: 0;
  z-index: 1;
  margin-left: calc(-1 * ${spacings.s});
  margin-right: calc(-1 * ${spacings.s});
  padding: ${spacings.s};
  background-image: linear-gradient(to bottom, ${color.hydrogen} 85%, transparent 85%);

  > * {
    width: 100%;
  }
`;

const StyledPopper = styled(Popper)`
  font: ${fonts.body2};
  display: flex;
  flex-direction: column;
  background-color: ${color.hydrogen};
  border-radius: ${radius};
  box-shadow: ${shadows.shadow100};
  padding: ${spacings.s};
  transition:
    transform ${animationTimeout}s cubic-bezier(0.18, 1.4, 0.4, 1),
    opacity ${animationTimeout / 2}s ease-in-out;
  min-width: 200px;
  /* max-width: 300px; */
  overflow-y: auto;

  &:has(.${MenuSearch}) {
    padding-top: 0;
  }

  &.enter {
    transform: scale(0.85);
    opacity: 0;
  }
  &.enter-active {
    opacity: 1;
    transform: scale(1);
  }
  &.exit {
    opacity: 1;
    transform: scale(1);
  }
  &.exit-active {
    transform: scale(0.85);
    opacity: 0;
  }
`;

const Menu = <T extends string>({
  children,
  overlay,
  placement = "bottom-start",
  // controlled
  opened: openedProp,
  setOpened: setOpenedProp,
  // uncontrolled
  defaultOpened = false,
  filtered = false,
  filterValue,
  onFilterChange,
  ...props
}: P<T>) => {
  const [opened, setOpened] = useControllableState(openedProp, setOpenedProp, defaultOpened);
  const handleButtonKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if ([" ", "ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
        setOpened(true);
      }
    },
    [setOpened],
  );
  const handleClick = React.useCallback(() => {
    setOpened(opened => !opened);
  }, [setOpened]);

  if (filtered && !onFilterChange)
    throw new Error("onFilterChange should always been declared with a filtered value");

  const handleChange = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange?.(e.target.value);
    },
    [onFilterChange],
  );

  return (
    <StyledPopper
      overlay={
        <OptionList onRequestClose={handleClick}>
          {filtered && (
            <div className={MenuSearch}>
              <SearchInputV2
                data-autofocus
                placeholder={t("placeholder.search")}
                value={filterValue}
                onChange={handleChange}
              />
            </div>
          )}
          {typeof overlay === "function" ? overlay() : overlay}
        </OptionList>
      }
      animationTimeout={animationTimeout}
      //   arrowWidth={8}
      placement={placement}
      opened={opened}
      setOpened={setOpened}
      gap={4}
      style={{ minWidth: props.matchChildWidth ? "unset" : "200px" }}
      {...props}
    >
      {React.cloneElement(children, {
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          handleClick();
        },
        onKeyDown: handleButtonKeyDown,
        className: classNames(children.props.className, {
          [OPENED]: opened,
        }),
      })}
    </StyledPopper>
  );
};

export default Menu;
